import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation} from '@github-ui/react-query'
import {useMarkersActions, usePullRequestBasePath} from '../stores/selectors'
import type {CommentLastContentEdit} from '../page-data/payloads/comment-interfaces'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

export type UpdateReviewCommentMutationResponse = {
  body: string
  bodyHTML: string
  bodyVersion: string
  commentDatabaseId: number
  threadId: number
  /** Authoritative last edit; `null` when the viewer cannot read edit history. */
  lastUserContentEdit?: CommentLastContentEdit | null
}

/**
 * Updates the body of a review comment
 */
export function useUpdateReviewCommentMutation({
  filePath,
  lineMarkersKey,
  onSuccess,
}: {
  filePath: string
  lineMarkersKey: LineKey | FileKey
  onSuccess?: () => void
}) {
  const basePath = usePullRequestBasePath()
  const apiUrl = `${basePath}/page_data/${PageData.updateReviewComment}`
  const {updateThreadComment} = useMarkersActions()

  return useMutation({
    mutationFn: async ({commentId, bodyVersion, body}: {commentId: string; bodyVersion?: string; body: string}) => {
      const response = await fetchWithErrorHandling(`${apiUrl}${bodyVersion ? `?body_version=${bodyVersion}` : ''}`, {
        method: 'PUT',
        body: {
          body,
          commentId,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: (data: UpdateReviewCommentMutationResponse) => {
      updateThreadComment({
        threadID: data.threadId,
        filePath,
        lineMarkersKey,
        match: storedComment => storedComment.databaseId === data.commentDatabaseId,
        transform: oldComment => {
          if (oldComment) {
            return {
              ...oldComment,
              body: data.body,
              bodyHTML: data.bodyHTML,
              bodyVersion: data.bodyVersion,
              ...(data.lastUserContentEdit !== undefined && {
                lastUserContentEdit: data.lastUserContentEdit && {
                  editor: data.lastUserContentEdit.editor,
                  id: data.lastUserContentEdit.id ?? undefined,
                },
              }),
            }
          }
        },
      })

      onSuccess?.()
    },
  })
}
